/**
 * Reading `audiowaveform`'s binary peak files, in one place.
 *
 * Lives in the template package because both sides need it: `apps/tanstack`
 * feeds the peaks to wavesurfer (see `apps/tanstack/src/utils/waveform.ts`,
 * which re-exports the parser and holds the wavesurfer-specific rules), and
 * `useFrequencyData` reads the whole-file peak out of the same file to keep the
 * windowed FFT on one amplitude scale.
 *
 * Deliberately a pure `.ts` with no DOM and no React: `packages/template` is
 * imported by Node-side code, and `types/layout.ts` keeps that rule.
 */

/**
 * Decodes an `audiowaveform` `.dat` buffer into wavesurfer channel data.
 *
 * Header is 20 bytes: version(4), channels(4), sample_rate(4), samples(4),
 * bits(4) — all little-endian. The body is 8-bit signed peaks, normalised to
 * [-1, 1], interleaved per channel.
 */
export function parseWaveformPeaks(buffer: ArrayBuffer): number[][] {
  const headerView = new DataView(buffer, 0, 20)
  const channels = headerView.getUint32(4, true)

  const dataView = new DataView(buffer, 20)
  const peaks: number[] = []

  for (let i = 0; i < dataView.byteLength; i++) {
    peaks.push(dataView.getInt8(i) / 128)
  }

  if (channels !== 2) return [peaks]

  const left: number[] = []
  const right: number[] = []
  for (let i = 0; i < peaks.length; i += 2) {
    left.push(peaks[i] || 0)
    right.push(peaks[i + 1] || 0)
  }
  return [left, right]
}

/**
 * The largest **positive** peak across every channel, or `0` when there is
 * none.
 *
 * Positive only, because this feeds `toInt16`, whose scaling is asymmetric
 * (`0x7fff` up, `0x8000` down) and which Remotion applies to the maximum
 * *positive* sample — see `frequencyScale.ts`. Taking `Math.abs` here would
 * compare a negative trough against a positive crest on the wrong scale.
 *
 * `0` is a real answer, not an error: a silent file, and an
 * opposite-phase stereo recording whose mono mix cancels, both legitimately
 * peak at nothing. The caller treats it as "no usable global peak".
 */
export function maxWaveformPeak(peaks: number[][]): number {
  let max = 0
  for (const channel of peaks) {
    for (const value of channel) {
      if (value > max) max = value
    }
  }
  return max
}
